import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';
import { CommonModule } from '@angular/common';
import { ButtonModule, OverlayPanelModule, TooltipModule } from 'primeng';
import { VmePipesModule } from '../../../vme-pipes.module';
import { ScheduleOptionsDateComponent } from '../schedule-options-date/schedule-options-date.component';
import { ScheduleOptionsTimeComponent } from '../schedule-options-time/schedule-options-time.component';
import { TravelSchedulerService } from '../../../services/travel-scheduler.service';
import {
  TimeSlot,
  TimeWindowResponse,
} from '../../../interfaces/travel-scheduler/time-window-response';

@Component({
  selector: 'schedule-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    OverlayPanelModule,
    VmePipesModule,
    ScheduleOptionsDateComponent,
    ScheduleOptionsTimeComponent,
    TooltipModule,
  ],
  templateUrl: './schedule-dropdown.component.html',
  styleUrls: ['./schedule-dropdown.component.css'],
})
export class ScheduleDropdownComponent implements OnInit, OnChanges {
  @Input() showCalendar: boolean = false;
  @Output() showCalendarChange = new EventEmitter<boolean>();

  @ViewChild('op') op: OverlayPanel;
  @ViewChild('trigger', { static: false }) triggerRef: ElementRef<HTMLElement>;

  protected selectedDate: Date;
  protected selectedTime: Date;

  @Output() scheduleSelected = new EventEmitter<Date>();
  @Input() selectedSchedule: Date = null;
  @Input() disabled: boolean = false;

  @Input() title: string = 'Select a Schedule';

  @Input() availableDates: TimeWindowResponse;

  protected warningMessage: string = '';

  constructor(private travelSchedulerService: TravelSchedulerService) {}

  ngOnInit(): void {}

  protected isSaveScheduleButtonDisabled(): boolean {
    if (this.selectedDate == null || this.selectedTime == null) {
      return true;
    }
    return false;
  }

  protected setSelectedDateTime() {
    if (this.selectedSchedule == null) {
      this.selectedDate = null;
      this.selectedTime = null;
    } else {
      this.selectedDate = new Date(this.selectedSchedule);
      this.selectedTime = new Date(this.selectedSchedule);
    }
    this.updateScheduleText();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedSchedule']) {
      this.setSelectedDateTime();
    }
  }

  protected toggleCalendar() {
    if (!this.disabled) {
      this.showCalendarChange.emit(!this.showCalendar);
    }
  }

  handleDateSelected(date: Date) {
    this.selectedDate = date;

    this.updateScheduleText();
  }

  handleTimeSelected(slot: TimeSlot) {
    this.selectedTime = slot.startTime;
    this.warningMessage = slot.warningMessage;
    this.updateScheduleText();
  }

  saveSchedule() {
    if (!this.selectedDate || !this.selectedTime) return;

    const merged = new Date(this.selectedDate); // clone the date part
    merged.setHours(this.selectedTime.getHours());
    merged.setMinutes(this.selectedTime.getMinutes());
    merged.setSeconds(this.selectedTime.getSeconds());
    merged.setMilliseconds(this.selectedTime.getMilliseconds());

    this.selectedSchedule = merged;
    this.scheduleSelected.emit(this.selectedSchedule);
    if (this.op) {
      this.op.hide();
    }
    this.showCalendarChange.emit(false);
  }

  onTriggerClick(event: Event, op: OverlayPanel) {
    if (this.disabled) {
      return;
    }
    op.toggle(event);
  }

  protected isOverlayVisible = false;

  private observer!: IntersectionObserver;

  onOverlayShow() {
    this.isOverlayVisible = true;
    this.showCalendarChange.emit(true);
    // Delay to ensure the overlay DOM is rendered before measuring
    setTimeout(() => this.setOverlayWidth(), 0);

    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    // set up intersection observer to move up the overlay if it escapes the viewport.
    const panelEl = (this.op as any).container;
    if (!panelEl) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        // If the overlay is not fully inside, reposition vertically
        if (!this.isFullyInside(entry.target as HTMLElement)) {
          this.keepOverlayInViewport(panelEl);
        }
      },
      { threshold: 1.0 },
    );

    this.observer.observe(panelEl);
  }

  private isFullyInside(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    return rect.top >= 0 && rect.left >= 0 && rect.bottom <= vh && rect.right <= vw;
  }

  private keepOverlayInViewport(panelEl: HTMLElement) {
    const rect = panelEl.getBoundingClientRect();
    const vh = window.innerHeight;

    let newTop = rect.top;

    if (rect.bottom > vh) {
      const overflow = rect.bottom - vh;
      newTop = rect.top - overflow - 4;
    }

    if (newTop < 0) {
      newTop = 4;
    }

    panelEl.style.top = `${newTop + window.scrollY}px`;
  }

  onOverlayHide() {
    this.saveSchedule();
    this.isOverlayVisible = false;
    this.showCalendarChange.emit(false);

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  onCloseClick(op: OverlayPanel) {
    op.hide();
  }

  /**
   * Adjusts the width of the calendar overlay panel to match the width of the trigger element.
   *
   * The overlay is appended to the document body, so it cannot inherit width styles (e.g., `w-full`)
   * from its parent container. This method ensures visual alignment by explicitly setting the
   * overlay's width based on the trigger element's rendered width.
   */
  private setOverlayWidth() {
    try {
      const triggerEl = this.triggerRef?.nativeElement as HTMLElement | undefined;
      const containerEl = (this.op as any)?.container as HTMLElement | undefined;
      if (!this.isOverlayVisible || !triggerEl || !containerEl) return;

      // Match overlay width to trigger's rendered width
      const width = triggerEl.offsetWidth;
      if (width && width > 0) {
        containerEl.style.width = width + 'px';
        containerEl.style.minWidth = width + 'px';
        containerEl.style.boxSizing = 'border-box';
      }
    } catch {}
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.isOverlayVisible) {
      // Defer to end of frame to allow layout to settle
      setTimeout(() => this.setOverlayWidth(), 0);
    }
  }

  protected scheduleText: string = this.title;

  protected updateScheduleText() {
    if (this.selectedDate == null) {
      this.scheduleText = this.title;
    } else {
      this.scheduleText = this.travelSchedulerService.formatScheduleWindowTextWithTime(
        this.selectedDate,
        this.selectedTime,
      );
    }
  }

  getAvailableTimes() {
    if (!this.availableDates || !this.selectedDate) {
      return [];
    }

    const times = this.availableDates.timeWindowLookup.find(
      (x) => x.date.getDate() === this.selectedDate.getDate(),
    )?.timeSlots;

    return times;
  }

  isTimeSelectionVisible() {
    return this.selectedDate != null;
  }
}
